import { useEffect, useRef } from 'react';
import { Navigation, Square, Wifi, WifiOff, Gauge, AlertTriangle, RefreshCw } from 'lucide-react';
import { useWaypointTracking } from '../../hooks/useWaypointTracking';
import LocationDisclosureModal from '../ui/LocationDisclosureModal';
import type { CadenceHints } from '../../utils/gpsCadence';

interface JobWaypointTrackerProps {
  jobId: number;
  isActive: boolean;
  onWaypointCount?: (count: number) => void;
  /**
   * If true (default), GPS tracking starts automatically whenever the job
   * becomes active. If the user previously declined location permissions
   * they can still tap Start tracking manually.
   */
  autoStart?: boolean;
  /** Implement width and job area, so points are recorded as often as the work needs. */
  hints?: CadenceHints;
}

function JobWaypointTracker({ jobId, isActive, onWaypointCount, autoStart = true, hints }: JobWaypointTrackerProps) {
  const t = useWaypointTracking('job', jobId, isActive, hints);
  const { tracking, startTracking, waypointCount } = t;

  // Notify parent of waypoint count changes
  if (onWaypointCount && waypointCount > 0) onWaypointCount(waypointCount);

  // Auto-start tracking when the job becomes active (once per active cycle).
  // If the operator explicitly stops, we don't restart until they pause and
  // resume the job itself. Narrow deps to the exact primitives used so this
  // doesn't fire on every GPS update.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!isActive) {
      autoStartedRef.current = false;
      return;
    }
    if (autoStart && !tracking && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startTracking();
    }
  }, [isActive, autoStart, tracking, startTracking]);

  if (!isActive) return null;

  const statusColors = {
    good: 'text-success-600',
    poor: 'text-amber-500',
    unavailable: 'text-neutral-400',
  };

  return (
    <>
    <LocationDisclosureModal
      open={t.showDisclosure}
      onContinue={t.acceptDisclosure}
    />
    <div className="bg-white border border-neutral-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Navigation className={`h-5 w-5 ${t.tracking ? 'text-green-600' : 'text-neutral-400'}`} />
          <span className="font-medium text-sm text-neutral-800">GPS Tracking</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-neutral-500">
          {t.gpsStatus === 'unavailable' ? (
            <WifiOff className="h-4 w-4 text-neutral-400" />
          ) : (
            <Wifi className={`h-4 w-4 ${statusColors[t.gpsStatus]}`} />
          )}
          <span className={statusColors[t.gpsStatus]}>
            {t.gpsStatus === 'good' ? 'Good GPS' : t.gpsStatus === 'poor' ? 'Weak signal' : 'No GPS'}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-600">
          <span className="font-bold text-neutral-900 text-lg">{t.waypointCount}</span> waypoints recorded
          {t.lastLocation && (
            <span className="text-xs text-neutral-400 block mt-0.5">
              Last: {t.lastLocation.lat.toFixed(5)}, {t.lastLocation.lng.toFixed(5)}
            </span>
          )}
          {t.pendingCount > 0 && (
            <span className="text-xs text-amber-500 block">{t.pendingCount} points pending upload</span>
          )}
        </div>

        {t.tracking ? (
          <button
            onClick={t.stopTracking}
            className="flex items-center gap-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-sm font-medium px-3 py-2 rounded-lg transition-colors"
          >
            <Square className="h-4 w-4" />
            Stop
          </button>
        ) : (
          <button
            onClick={t.startTracking}
            disabled={!isActive}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            <Navigation className="h-4 w-4" />
            Start tracking
          </button>
        )}
      </div>

      {t.currentSpeedKmh != null && t.tracking && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-neutral-500">
          <Gauge className="h-3.5 w-3.5" />
          <span>{t.currentSpeedKmh.toFixed(1)} km/h</span>
        </div>
      )}

      {t.cadenceLabel && (
        <p className="mt-1 text-xs text-neutral-400">{t.cadenceLabel}</p>
      )}

      {t.error && (
        <p className="text-xs text-amber-600 mt-2 bg-amber-50 rounded px-2 py-1">{t.error}</p>
      )}

      {/* Upload failure banner — points are in IndexedDB but we can't send
          them to the server. Shows the reason + a manual retry.
          A 409 means the assignment is paused/completed: the queue self-
          heals on resume (records flush automatically), so we surface a
          calmer message instead of a generic upload-failed warning. */}
      {t.pendingCount > 0 && t.uploadError && t.uploadFailures >= 2 && (
        t.uploadError.status === 409 ? (
          <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded px-2 py-1.5 text-xs text-blue-800">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium">Job paused on the server</p>
              <p className="text-blue-700 truncate">
                {t.pendingCount} point{t.pendingCount === 1 ? '' : 's'} will upload when you resume the job.
              </p>
            </div>
            <button
              onClick={t.retryUpload}
              className="flex items-center gap-1 bg-blue-200 hover:bg-blue-300 text-blue-900 font-medium px-2 py-1 rounded flex-shrink-0"
            >
              <RefreshCw className="h-3 w-3" />
              Resume
            </button>
          </div>
        ) : (
          <div className="mt-2 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-xs text-amber-800">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium">Can't upload points</p>
              <p className="text-amber-700 truncate">
                {t.uploadError.status ? `[${t.uploadError.status}] ` : ''}{t.uploadError.message}
              </p>
            </div>
            <button
              onClick={t.retryUpload}
              className="flex items-center gap-1 bg-amber-200 hover:bg-amber-300 text-amber-900 font-medium px-2 py-1 rounded flex-shrink-0"
            >
              <RefreshCw className="h-3 w-3" />
              Retry
            </button>
          </div>
        )
      )}

      {t.tracking && (
        <p className="text-xs text-neutral-400 mt-2">
          Background GPS active &middot; Data saved offline
        </p>
      )}
    </div>
    </>
  );
}

export default JobWaypointTracker;
